import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { Mail, Phone, Building2, MessageCircle, Inbox } from "lucide-react";

const STATUSES = [
  { v: "new", label: "New", pill: "warn" },
  { v: "contacted", label: "Contacted", pill: "" },
  { v: "qualified", label: "Qualified", pill: "" },
  { v: "converted", label: "Converted", pill: "ok" },
  { v: "rejected", label: "Rejected", pill: "err" },
];

export default function Leads() {
  const { notify } = useDialog();
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/leads");
    setLeads(data.leads || []);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (lid, status) => {
    try { await api.put(`/admin/leads/${lid}`, { status }); load(); }
    catch (e) { await notify({ title: "Update failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  const filtered = filter ? leads.filter((l) => l.status === filter) : leads;
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s.v]: leads.filter((l) => l.status === s.v).length }), {});
  const newCount = counts.new || 0;

  const pillFor = (s) => STATUSES.find((x) => x.v === s)?.pill || "";

  return (
    <div className="vb-fade-in" data-testid="leads-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>
            <Inbox size={26} className="inline mr-2" /> Service enquiries
          </h1>
          <p className="opacity-70">Inbound leads from the public enquiry form. Triage and convert them into customer tenants.</p>
        </div>
        {newCount > 0 && (
          <span className="vb-pill warn" data-testid="leads-new-count">
            {newCount} new {newCount === 1 ? "enquiry" : "enquiries"}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="leads-filters">
        <button className={`vb-btn ${filter === "" ? "" : "ghost"}`} onClick={() => setFilter("")}>All ({leads.length})</button>
        {STATUSES.map((s) => (
          <button key={s.v} className={`vb-btn ${filter === s.v ? "" : "ghost"}`} onClick={() => setFilter(s.v)} data-testid={`filter-${s.v}`}>
            {s.label} ({counts[s.v] || 0})
          </button>
        ))}
      </div>

      <div className="vb-card overflow-x-auto"><table className="vb-table">
        <thead><tr><th>Received</th><th>Company</th><th>Contact</th><th>Email / Phone</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {filtered.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: "center", padding: 36 }} className="opacity-60">
              No enquiries{filter ? ` with status "${filter}"` : ""} yet.
            </td></tr>
          )}
          {filtered.map((l) => (
            <tr key={l.id} data-testid={`lead-row-${l.id}`}>
              <td className="opacity-70">{new Date(l.created_at).toLocaleString()}</td>
              <td><strong>{l.company_name}</strong></td>
              <td>{l.name}</td>
              <td className="text-xs">
                <div><Mail size={11} className="inline mr-1" />{l.email}</div>
                {l.phone && <div className="opacity-70"><Phone size={11} className="inline mr-1" />{l.phone}</div>}
              </td>
              <td><span className={`vb-pill ${pillFor(l.status)}`}>{l.status}</span></td>
              <td>
                <button className="vb-btn ghost" onClick={() => setActive(l)} data-testid={`open-lead-${l.id}`}>Open</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>

      {active && (
        <div className="vb-modal-bg" onClick={() => setActive(null)} data-testid="lead-detail">
          <div className="vb-modal" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <div>
                <h2 className="m-0 flex items-center gap-2"><Building2 size={18} /> {active.company_name}</h2>
                <p className="text-xs opacity-60 mt-1">Received {new Date(active.created_at).toLocaleString()}</p>
              </div>
              <span className={`vb-pill ${pillFor(active.status)}`}>{active.status}</span>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 my-4">
              <div className="vb-card p-3"><div className="text-xs opacity-60">Contact</div><strong>{active.name}</strong></div>
              <div className="vb-card p-3"><div className="text-xs opacity-60">Email</div>{active.email}</div>
              <div className="vb-card p-3"><div className="text-xs opacity-60">Phone</div>{active.phone || "—"}</div>
            </div>

            <div className="vb-card p-3 mb-4">
              <div className="text-xs opacity-60 mb-1 flex items-center gap-1"><MessageCircle size={12} /> Their message</div>
              <p className="whitespace-pre-wrap">{active.message}</p>
            </div>

            <h3 className="vb-heading text-lg mb-2" style={{ color: "var(--vb-text-2)" }}>Move to status</h3>
            <div className="flex flex-wrap gap-2 mb-4">
              {STATUSES.map((s) => (
                <button
                  key={s.v}
                  className={`vb-btn ${active.status === s.v ? "" : "ghost"}`}
                  disabled={active.status === s.v}
                  onClick={async () => { await updateStatus(active.id, s.v); setActive({ ...active, status: s.v }); }}
                  data-testid={`set-status-${s.v}`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <p className="text-xs opacity-60 mb-3">
              💡 Once qualified, head to <strong>Tenants → New tenant</strong> to provision their workspace, then mark this lead as Converted.
            </p>

            <div className="text-right"><button className="vb-btn" onClick={() => setActive(null)}>Close</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
